import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Settings as SettingsIcon, Shield, Database, Bell, Building2,
  Clock, Users, Plus, Trash2, Edit, Download, Upload,
  Mail, MessageSquare, Smartphone, Cloud, HardDrive,
  Key, Save, RefreshCw, AlertTriangle, Check, X, Euro
} from 'lucide-react';
import axios from 'axios';
import { 
  WorktimeSettingsTab, 
  NotificationSettingsTab, 
  BackupSettingsTab,
  AddEmployeeLoginModal,
  AddAdminModal,
  EditAdminModal,
  ChangePasswordModal
} from './SettingsComponents';
import PriceList from './PriceList';
import BillingPositions from './BillingPositions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [worktimeSettings, setWorktimeSettings] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [employeeLogins, setEmployeeLogins] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [backupList, setBackupList] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [showAddEmployeeLogin, setShowAddEmployeeLogin] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPriceList, setShowPriceList] = useState(false);
  const [showBillingPositions, setShowBillingPositions] = useState(false);

  // Load data
  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [
        companyResp, worktimeResp, notificationResp, 
        loginResp, adminResp, backupResp, employeesResp
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/settings/company`, config),
        axios.get(`${BACKEND_URL}/api/settings/worktime`, config),
        axios.get(`${BACKEND_URL}/api/settings/notifications`, config),
        axios.get(`${BACKEND_URL}/api/settings/employee-logins`, config),
        axios.get(`${BACKEND_URL}/api/settings/admin-users`, config),
        axios.get(`${BACKEND_URL}/api/settings/backup/list`, config),
        axios.get(`${BACKEND_URL}/api/employees`, config)
      ]);

      setCompanySettings(companyResp.data);
      setWorktimeSettings(worktimeResp.data);
      setNotificationSettings(notificationResp.data);
      setEmployeeLogins(loginResp.data);
      setAdminUsers(adminResp.data);
      setBackupList(backupResp.data);
      setEmployees(employeesResp.data);
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen:', error);
      if (error.response?.status === 403) {
        alert('Nur Administratoren dürfen auf Einstellungen zugreifen');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (endpoint, data) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${BACKEND_URL}/api/settings/${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Einstellungen erfolgreich gespeichert!');
      loadAllSettings(); // Reload data
    } catch (error) {
      alert('Fehler beim Speichern: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const createBackup = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${BACKEND_URL}/api/settings/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Backup erfolgreich erstellt!');
      loadAllSettings(); // Reload backup list
    } catch (error) {
      alert('Backup-Erstellung fehlgeschlagen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-full bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <SettingsIcon className="w-12 h-12 text-emerald-600 mx-auto animate-spin" />
          <p className="text-slate-600 mt-2">Lädt Einstellungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <SettingsIcon className="w-8 h-8 text-emerald-600" />
            Einstellungen ⚙️
          </h1>
          <p className="text-slate-600 mt-1">Systemkonfiguration und Benutzereinstellungen (Nur für Administratoren)</p>
        </div>
        <Button 
          onClick={loadAllSettings}
          variant="outline"
          className="gap-2"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Tab Navigation */}
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Firma
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Benutzer
          </TabsTrigger>
          <TabsTrigger value="worktime" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Arbeitszeiten
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Benachrichtigungen
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Backup & Export
          </TabsTrigger>
          <TabsTrigger value="pricelist" className="flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Preisliste
          </TabsTrigger>
          <TabsTrigger value="billing-positions" className="flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Abrechnungspositionen
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <CompanySettingsTab 
            settings={companySettings} 
            onSave={(data) => saveSettings('company', data)}
          />
        </TabsContent>

        {/* User Management */}
        <TabsContent value="users">
          <UserManagementTab
            employeeLogins={employeeLogins}
            adminUsers={adminUsers}
            employees={employees}
            onReload={loadAllSettings}
          />
        </TabsContent>

        {/* Worktime Settings */}
        <TabsContent value="worktime">
          <WorktimeSettingsTab
            settings={worktimeSettings}
            onSave={(data) => saveSettings('worktime', data)}
          />
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <NotificationSettingsTab
            settings={notificationSettings}
            onSave={(data) => saveSettings('notifications', data)}
          />
        </TabsContent>

        {/* Backup & Export */}
        <TabsContent value="backup">
          <BackupSettingsTab
            backupList={backupList}
            onCreateBackup={createBackup}
            onReload={loadAllSettings}
          />
        </TabsContent>

        {/* Price List */}
        <TabsContent value="pricelist">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Euro className="w-5 h-5 text-emerald-600" />
                  Preisliste für abrechenbare Arbeiten & Materialien
                </span>
                <Button 
                  onClick={() => setShowPriceList(true)}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Euro className="w-4 h-4" />
                  Preisliste öffnen
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-900 mb-2">📋 Zweck der Preisliste</h3>
                  <p className="text-sm text-emerald-800">
                    Hier können Sie Artikelnummern, Bezeichnungen und Einzelpreise für abrechenbare Arbeiten 
                    und Materialien verwalten. Diese Preise werden für die Berechnung der Technikereinnahmen verwendet.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        📦
                      </div>
                      <h4 className="font-medium">Material</h4>
                    </div>
                    <p className="text-sm text-slate-600">
                      Kabel, Steckdosen, Schalter, Verbrauchsmaterialien
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        🔧
                      </div>
                      <h4 className="font-medium">Dienstleistung</h4>
                    </div>
                    <p className="text-sm text-slate-600">
                      Montage, Installation, Reparatur, Wartung
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        ⚙️
                      </div>
                      <h4 className="font-medium">Gerät</h4>
                    </div>
                    <p className="text-sm text-slate-600">
                      Werkzeuge, Messgeräte, Spezialausrüstung
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">💡 Funktionen</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>✓ Artikel mit Nummern, Bezeichnungen und Preisen hinzufügen</li>
                    <li>✓ Inline-Bearbeitung für schnelle Änderungen</li>
                    <li>✓ Kategorien für bessere Organisation (benutzerdefiniert)</li>
                    <li>✓ Suche nach Artikelnummer, Name oder Beschreibung</li>
                    <li>✓ Filterung nach Typ und Kategorie</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Positions Tab */}
        <TabsContent value="billing-positions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Euro className="w-5 h-5 text-emerald-600" />
                  Abrechnungspositionen für Techniker
                </span>
                <Button 
                  onClick={() => setShowBillingPositions(true)}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Euro className="w-4 h-4" />
                  Positionen verwalten
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-900 mb-2">📋 Zweck der Abrechnungspositionen</h3>
                  <p className="text-sm text-emerald-800">
                    Diese Positionen werden von Technikern verwendet, um ihre täglichen Aufträge zu dokumentieren 
                    und abzurechnen. Jede Position hat eine Nummer, Beschreibung, Einheit und einen Einzelpreis.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        🔌
                      </div>
                      <h4 className="font-medium">GWV-Aufträge</h4>
                    </div>
                    <p className="text-sm text-slate-600">
                      Glasfaser-Anschlüsse, Installations- und Montagearbeiten
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        📡
                      </div>
                      <h4 className="font-medium">Connect-Aufträge</h4>
                    </div>
                    <p className="text-sm text-slate-600">
                      Netzwerk-Verbindungen, Kabelverlege-Arbeiten
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        🔧
                      </div>
                      <h4 className="font-medium">Zusatzleistungen</h4>
                    </div>
                    <p className="text-sm text-slate-600">
                      Wartung, Reparatur, Kundendienst
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">💡 Verwendung</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>✓ Techniker wählen Positionen im Kalender für ihre Abrechnung</li>
                    <li>✓ Jede Position kann mit Mengenangabe verwendet werden</li>
                    <li>✓ Automatische Berechnung von Zwischensummen und Gesamtsumme</li>
                    <li>✓ SMILE-Nummern werden für jeden Auftrag erfasst</li>
                    <li>✓ Bestätigte Abrechnungen sind für Techniker nicht mehr bearbeitbar</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Price List Modal */}
      <PriceList 
        isOpen={showPriceList} 
        onClose={() => setShowPriceList(false)} 
      />

      {/* Billing Positions Modal */}
      <BillingPositions 
        isOpen={showBillingPositions} 
        onClose={() => setShowBillingPositions(false)} 
      />
    </div>
  );
};

// Company Settings Component
const CompanySettingsTab = ({ settings, onSave }) => {
  const [formData, setFormData] = useState(settings || {});

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Firmeneinstellungen
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Firmenname</label>
              <Input
                value={formData.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="SKALARTECH GmbH"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Telefon</label>
              <Input
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+49 123 456789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">E-Mail</label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@skalartech.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Website</label>
              <Input
                value={formData.website || ''}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://www.skalartech.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Straße</label>
              <Input
                value={formData.address_street || ''}
                onChange={(e) => handleChange('address_street', e.target.value)}
                placeholder="Musterstraße 123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">PLZ</label>
              <Input
                value={formData.address_zip || ''}
                onChange={(e) => handleChange('address_zip', e.target.value)}
                placeholder="12345"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Stadt</label>
              <Input
                value={formData.address_city || ''}
                onChange={(e) => handleChange('address_city', e.target.value)}
                placeholder="Berlin"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Land</label>
              <Input
                value={formData.address_country || ''}
                onChange={(e) => handleChange('address_country', e.target.value)}
                placeholder="Deutschland"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Steuernummer</label>
              <Input
                value={formData.tax_number || ''}
                onChange={(e) => handleChange('tax_number', e.target.value)}
                placeholder="123/456/789"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Speichern
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// User Management Component
const UserManagementTab = ({ employeeLogins, adminUsers, employees, onReload }) => {
  const [showAddEmployeeLogin, setShowAddEmployeeLogin] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showEditAdmin, setShowEditAdmin] = useState(false);

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setShowEditAdmin(true);
  };

  const handleDeleteAdmin = async (admin) => {
    if (admin.username === 'admin') {
      alert('Der Haupt-Administrator-Account kann nicht gelöscht werden');
      return;
    }

    if (window.confirm(`Sind Sie sicher, dass Sie den Admin-Benutzer "${admin.username}" löschen möchten?`)) {
      try {
        const token = localStorage.getItem('authToken');
        await axios.delete(`${BACKEND_URL}/api/settings/admin-users/${admin.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Admin-Benutzer erfolgreich gelöscht');
        onReload();
      } catch (error) {
        alert('Fehler beim Löschen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Admin Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Administrator-Accounts ({adminUsers.length})
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowChangePassword(true)}
                className="flex items-center gap-2"
              >
                <Key className="w-4 h-4" />
                Passwort ändern
              </Button>
              <Button onClick={() => setShowAddAdmin(true)} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Admin hinzufügen
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {adminUsers.map((admin) => (
              <div key={admin.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                <div className="flex-1">
                  <p className="font-medium">{admin.username}</p>
                  <p className="text-sm text-slate-500">{admin.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">Admin</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditAdmin(admin)}
                    className="p-2"
                    title="Bearbeiten"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAdmin(admin)}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={admin.username === 'admin'}
                    title={admin.username === 'admin' ? 'Haupt-Admin kann nicht gelöscht werden' : 'Löschen'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Employee Logins */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Mitarbeiter-Logins ({employeeLogins.length})
            </span>
            <Button onClick={() => setShowAddEmployeeLogin(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Login hinzufügen
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employeeLogins.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">Keine Mitarbeiter-Logins erstellt</p>
            </div>
          ) : (
            <div className="space-y-3">
              {employeeLogins.map((login) => (
                <EmployeeLoginItem 
                  key={login.id} 
                  login={login} 
                  onReload={onReload}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {showAddEmployeeLogin && (
        <AddEmployeeLoginModal
          employees={employees.filter(emp => !employeeLogins.find(login => login.employee_id === emp.id))}
          onClose={() => setShowAddEmployeeLogin(false)}
          onSuccess={() => {
            setShowAddEmployeeLogin(false);
            onReload();
          }}
        />
      )}

      {showAddAdmin && (
        <AddAdminModal
          onClose={() => setShowAddAdmin(false)}
          onSuccess={() => {
            setShowAddAdmin(false);
            onReload();
          }}
        />
      )}

      {showEditAdmin && editingAdmin && (
        <EditAdminModal
          admin={editingAdmin}
          onClose={() => {
            setShowEditAdmin(false);
            setEditingAdmin(null);
          }}
          onSuccess={() => {
            setShowEditAdmin(false);
            setEditingAdmin(null);
            onReload();
          }}
        />
      )}

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
};

// Employee Login Item Component
const EmployeeLoginItem = ({ login, onReload }) => {
  const deleteLogin = async () => {
    if (!window.confirm('Möchten Sie diesen Mitarbeiter-Login wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/settings/employee-logins/${login.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Login erfolgreich gelöscht');
      onReload();
    } catch (error) {
      alert('Fehler beim Löschen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div>
        <p className="font-medium">{login.username}</p>
        <p className="text-sm text-slate-500">
          {login.employee_name} ({login.employee_number})
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={login.is_active ? "default" : "secondary"}>
          {login.is_active ? "Aktiv" : "Inaktiv"}
        </Badge>
        <Button
          variant="destructive"
          size="sm"
          onClick={deleteLogin}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Modal components are imported from SettingsComponents.js

// AddAdminModal imported from SettingsComponents.js

// ChangePasswordModal imported from SettingsComponents.js

// WorktimeSettingsTab imported from SettingsComponents.js

// NotificationSettingsTab imported from SettingsComponents.js

// BackupSettingsTab imported from SettingsComponents.js

export default Settings;